import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';

export const ConsumerServiceOrderItemSchema = z.object({
  id: z.uuid(),
  itemType: z.enum(['PRODUCT', 'SERVICE', 'GENERIC_PRODUCT']),
  description: z.string().min(1),
  quantity: z.string().min(1),
  unitPriceCents: z.string().min(1).nullable(),
  totalCents: z.string().min(1),
  currency: z.string().min(1),
  executionStatus: z.string().min(1),
  approvedByCustomer: z.boolean(),
  rejectedByCustomer: z.boolean(),
});
export type ConsumerServiceOrderItem = z.infer<typeof ConsumerServiceOrderItemSchema>;

export const ConsumerServiceOrderListItemSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  partyId: z.uuid(),
  workshopName: z.string().min(1),
  status: z.string().min(1),
  displayNumber: z.string().min(1),
  title: z.string().min(1),
  openedAt: z.string().min(1),
  deliveredAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  totalCents: z.string().min(1),
  currency: z.string().min(1),
  vehiclePlate: z.string().nullable(),
});
export type ConsumerServiceOrderListItem = z.infer<typeof ConsumerServiceOrderListItemSchema>;

/** Consumer-facing list chips (mobile Serviços). */
export const ConsumerServiceOrderListStatusFilterSchema = z.enum([
  'all',
  'active',
  'awaiting',
  'done',
]);
export type ConsumerServiceOrderListStatusFilter = z.infer<
  typeof ConsumerServiceOrderListStatusFilterSchema
>;

export const ConsumerServiceOrdersQuerySchema = PaginationSchema.extend({
  status: ConsumerServiceOrderListStatusFilterSchema.optional(),
  tenantId: z.uuid().optional(),
  q: z.string().trim().min(1).max(100).optional(),
});
export type ConsumerServiceOrdersQuery = z.infer<typeof ConsumerServiceOrdersQuerySchema>;

export const ConsumerServiceOrdersResponseSchema = z.object({
  items: z.array(ConsumerServiceOrderListItemSchema),
  pagination: CursorPaginationMetaSchema,
});
export type ConsumerServiceOrdersResponse = z.infer<typeof ConsumerServiceOrdersResponseSchema>;

/** Media (photo/video) attached to an OS — from check-in damages or quote attachments. */
export const ConsumerServiceOrderMediaSchema = z.object({
  id: z.uuid(),
  origin: z.enum(['CHECK_IN', 'QUOTE']),
  mediaType: z.enum(['IMAGE', 'VIDEO']),
  contentType: z.string().min(1).nullable(),
  objectUrl: z.string().min(1),
  caption: z.string().nullable(),
});
export type ConsumerServiceOrderMedia = z.infer<typeof ConsumerServiceOrderMediaSchema>;

export const ConsumerServiceOrderDetailSchema = ConsumerServiceOrderListItemSchema.extend({
  cancelledAt: z.string().nullable(),
  items: z.array(ConsumerServiceOrderItemSchema),
  media: z.array(ConsumerServiceOrderMediaSchema),
});
export type ConsumerServiceOrderDetail = z.infer<typeof ConsumerServiceOrderDetailSchema>;

export const ConsumerServiceOrderItemDecisionSchema = z.enum(['APPROVED', 'REJECTED']);
export type ConsumerServiceOrderItemDecision = z.infer<
  typeof ConsumerServiceOrderItemDecisionSchema
>;

export const ConsumerServiceOrderItemDecisionBodySchema = z.object({
  decision: ConsumerServiceOrderItemDecisionSchema,
});
export type ConsumerServiceOrderItemDecisionBody = z.infer<
  typeof ConsumerServiceOrderItemDecisionBodySchema
>;

export const ConsumerServiceOrderItemDecisionsBodySchema = z.object({
  decisions: z
    .array(
      z.object({
        itemId: z.uuid(),
        decision: ConsumerServiceOrderItemDecisionSchema,
      }),
    )
    .min(1),
});
export type ConsumerServiceOrderItemDecisionsBody = z.infer<
  typeof ConsumerServiceOrderItemDecisionsBodySchema
>;
